import type { AdapterFetchOptions, AdapterResult, MarketSourceAdapter } from '@/types/adapters';
import type { NormalizedMarketRow } from '@/types/market';
import { appConfig } from '@/config/app';
import { SOURCE_REGISTRY } from '@/data/sourceRegistry';
import { loadSnapshotManifest } from '@/data/snapshotManifest';
import { fetchCsv, parseCsv } from '@/utils/csv';
import { toNum } from '@/utils/numbers';

const DEFAULT_FILE = 'nar_metro_quarterly_sample.csv';
const EXPECTED_FIELDS = 5;

/**
 * NAR adapter: coarser, quarterly, fewer fields than TRERC. Used mainly as
 * a corroborating/backfill source when TRERC is missing a county, and as a
 * second opinion in the confidence-score calculation.
 */
export const narAdapter: MarketSourceAdapter = {
  meta: SOURCE_REGISTRY.nar,

  async fetch(options?: AdapterFetchOptions): Promise<AdapterResult> {
    const warnings: string[] = [];
    const manifest = await loadSnapshotManifest(options?.signal);
    const entry = manifest.nar;
    const file = entry?.file ?? DEFAULT_FILE;
    const isSampleData = entry?.isSample ?? true;

    let text: string;
    try {
      text = await fetchCsv(`${appConfig.dataSnapshotBasePath}/${file}`, options?.signal);
    } catch (err) {
      warnings.push(`nar: could not load ${file}: ${(err as Error).message}`);
      return { rows: [], warnings, isSampleData };
    }

    const records = parseCsv(text);
    const rows: NormalizedMarketRow[] = [];

    for (const r of records) {
      const geoId = r.county_fips?.trim();
      if (!geoId) continue;

      const medianSalePrice = toNum(r.median_sale_price);
      const medianDaysOnMarket = toNum(r.dom_median);
      const activeListings = toNum(r.active_listings);
      const monthsOfInventory = toNum(r.months_inventory);
      const closedSales = toNum(r.closed_sales);

      const filledCount = [medianSalePrice, medianDaysOnMarket, activeListings, monthsOfInventory, closedSales].filter(
        (v) => v !== undefined,
      ).length;

      rows.push({
        geoLevel: 'county',
        geoId,
        geoName: r.county_name?.trim() || geoId,
        sourceId: 'nar',
        sourceLabel: entry?.label ?? SOURCE_REGISTRY.nar.label,
        periodLabel: r.period?.trim() || entry?.periodLabel || 'unknown',
        publishedAt: r.published_at?.trim() || entry?.publishedAt || new Date().toISOString().slice(0, 10),
        retrievedAt: new Date().toISOString(),
        medianSalePrice,
        medianDaysOnMarket,
        activeListings,
        monthsOfInventory,
        closedSales,
        fieldCompleteness: filledCount / EXPECTED_FIELDS,
      });
    }

    return { rows, warnings, isSampleData };
  },
};
